import React, { useState, useEffect } from "react";
import Iconify from "../../components/Iconify";
import { Button, Avatar, Tooltip } from "@mui/material";
import CustomTable from "../../components/customTable/CustomTable";
import { useSnackbar } from "notistack";
import { useAdminContext } from "../../Hooks/AdminContext";
import StatusChip from "../../theme/chip";
import DeleteConfirmation from "../../components/DeleteConfirmation";
import { useNavigate } from "react-router-dom";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import CustomCircularProgress from "../../components/CustomCircularProgress";
import {
  _Delete_Employee,
  _Employee_Lists,
} from "../../DAL/Listing/EmployeeList";
import { show_proper_words } from "../../utils/constant";
import { picBaseUrl } from "../../config/config";

const EmployeeManagement = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { setnavbarTitle } = useAdminContext();
  const { enqueueSnackbar } = useSnackbar();
  const [rowDataID, setRowDataID] = useState("");
  const [rowData, setRowData] = useState({});
  const [openDelete, setOpenDelete] = useState(false);

  const [userList, setUserList] = useState([]);
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [pageCount, setPageCount] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);

  const handleOpenModal = () => {
    navigate(`/employee-management/add-employee`);
  };

  // This function handles the deletion of an employee. It sets loading state to true,
  // sends a request to delete the employee by calling `_Delete_Employee` with the employee's ID.
  // If the deletion is successful, it updates the user list by removing the deleted employee,
  // displays a success message, and decrements the total employee count.
  // If the deletion fails, it displays an error message. Finally, it hides the delete modal and resets the loading state.
  const handleDelete = async () => {
    setIsLoading(true);
    const response = await _Delete_Employee(rowDataID);
    if (response) {
      const updatedUserList = userList.filter((user) => user._id !== rowDataID);
      setUserList(updatedUserList);
      enqueueSnackbar("Employee deleted successfully", { variant: "success" });
      setTotalCount((prev) => prev - 1);
      setOpenDelete(false);
    } else {
      enqueueSnackbar("Failed to delete employee", { variant: "error" });
    }
    setIsLoading(false);
  };

  // This function sets the ID and data of the employee to be deleted when the user agrees to delete.
  // It updates the state with the selected employee's ID and data, and then opens the delete confirmation modal.
  const handleAgreeDelete = (row) => {
    setRowDataID(row._id);
    setRowData(row);
    setOpenDelete(true);
  };

  // This function handles the search functionality for the employee list.
  // It prevents the default form submission behavior, updates the search text in state,
  // stores the search text in local storage, resets the page to 0, and calls the
  // GetEmployeeList function to fetch the filtered list based on the search text.
  const searchFunction = (e) => {
    e.preventDefault();
    setSearchText(searchText);
    localStorage.setItem("searchText_Employee_Managements", searchText);
    setPage(0);
    setPageCount(1);
    GetEmployeeList(0, rowsPerPage, searchText);
  };

  // This function handles page changes for pagination.
  // It updates the page state to the new page number and adjusts the page count accordingly.
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    if (newPage <= 0) {
      setPageCount(1);
    } else {
      setPageCount(newPage + 1);
    }
  };

  // This function handles changes in the number of rows per page for pagination.
  // It updates the rowsPerPage state to the new value and resets the page state to 0.
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // This function handles the page change for pagination.
  // If the new page is less than or equal to 0, it resets the page to 0 and sets the page count to 1.
  // Otherwise, it updates the page state to the new page and adjusts the page count accordingly.
  const handleChangePages = (event, newPage) => {
    if (newPage <= 0) {
      setPage(0);
      setPageCount(1);
    } else {
      setPage(newPage - 1);
      setPageCount(newPage);
    }
  };

  // The `User_Table_Head` defines the table header configuration for the employee management table.
  // Each object represents a column in the table with specific properties:
  // - `id`: The unique identifier for the column
  // - `label`: The column title displayed in the table header
  // - `renderData`: A function that renders the content for each cell in that column based on the row data.
  // Some columns include tooltips for actions like editing and deleting, and others render avatar images or status chips for user status.
  const User_Table_Head = [
    {
      id: "action",
      label: "Action",
      renderData: (row) => (
        <>
          <Tooltip title="Edit" placement="top">
            <EditIcon
              onClick={() => {
                navigate(`/employee-management/edit-employee/${row?._id}`, {
                  state: row,
                });
              }}
              style={{ fill: "#fff", marginRight: "10px", cursor: "pointer" }}
            />
          </Tooltip>
          <Tooltip title="Delete" placement="top">
            <DeleteOutlineIcon
              onClick={() => {
                handleAgreeDelete(row);
              }}
              style={{
                fill: "#ff0000",
                marginRight: "10px",
                cursor: "pointer",
              }}
            />
          </Tooltip>
        </>
      ),
    },
    {
      id: "number",
      label: "#",
      type: "number",
    },
    {
      id: "avatar",
      label: "Image",
      renderData: (row) => (
        <Avatar
          alt={row.name}
          src={picBaseUrl + row?.profile_image || "/default-avatar.png"}
          sx={{ width: 40, height: 40 }}
        />
      ),
    },
    {
      id: "name",
      label: "Name",
      renderData: (row) => (
        <span>{show_proper_words(row.first_name + " " + row.last_name)}</span>
      ),
    },
    {
      id: "email",
      label: "Email",
    },
    {
      id: "phone",
      label: "Phone Number",
      renderData: (row) => <span>{row.contact_number || "_ _"}</span>,
    },

    {
      id: "status",
      label: "Status",
      renderData: (row) => (
        <StatusChip status={row.status} type="activeInactive" />
      ),
    },
  ];

  // The `GetEmployeeList` function fetches a list of employees based on the provided search text, page number, and rows per page.
  // It constructs a request with the `searchText` parameter and sends a request to get employee data.
  // If the response is successful, it updates the state with the employee list, total count, and total pages.
  // In case of an error, it displays an error message and stops the loading state.
  const GetEmployeeList = async (page, rowsPerPage, searchText) => {
    setLoading(true);
    const FormData = {
      search: searchText,
    };
    const response = await _Employee_Lists(FormData, page, rowsPerPage);
    if (response.code === 200) {
      const { employee_list, total_pages, total_count } = response;
      setUserList(employee_list);
      setTotalCount(total_count);
      setTotalPages(total_pages);
      setLoading(false);
    } else {
      setLoading(false);
      enqueueSnackbar(response.message, { variant: "error" });
    }
  };

  // The `useEffect` hook is triggered whenever `page` or `rowsPerPage` changes.
  // It retrieves the search text from localStorage if available, which stores the last search text for the employee management page.
  // It then updates the navbar title to "Employee Management" and calls `GetEmployeeList` to fetch the employee data.
  // If search text is found in localStorage, it is used to fetch the employee list; otherwise, the current `searchText` is used.
  useEffect(() => {
    const searcthecxt = localStorage.getItem("searchText_Employee_Managements");
    setnavbarTitle("Employee Management");
    if (searcthecxt) {
      GetEmployeeList(page, rowsPerPage, searcthecxt);
    } else {
      GetEmployeeList(page, rowsPerPage, searchText);
    }
  }, [page, rowsPerPage]);

  if (loading) {
    return <CustomCircularProgress />;
  }

  return (
    <div className="mt-4">
      <DeleteConfirmation
        isLoading={isLoading}
        open={openDelete}
        setRowData={setRowData}
        setRowDataID={setRowDataID}
        setOpen={setOpenDelete}
        title={"Are you sure you want to delete this employee?"}
        handleAgree={handleDelete}
        handleClose={() => setOpenDelete(false)}
      />

      {/* Add New Employee and Investigator Buttons */}
      <div className="d-flex justify-content-end mb-3 add-button">
        <Button
          variant="contained"
          startIcon={<Iconify icon="eva:plus-fill" />}
          onClick={handleOpenModal}
        >
          Create New Employee
        </Button>
      </div>

      {/* Employee List Table */}
      <div className="mt-3">
        <CustomTable
          data={userList}
          TABLE_HEAD={User_Table_Head}
          MENU_OPTIONS={[]}
          custom_search={{
            searchText: searchText,
            setSearchText: setSearchText,
            handleSubmit: searchFunction,
          }}
          custom_pagination={{
            total_count: totalCount,
            rows_per_page: rowsPerPage,
            page: page,
            handleChangePage: handleChangePage,
            onRowsPerPageChange: handleChangeRowsPerPage,
          }}
          pageCount={pageCount}
          totalPages={totalPages}
          pagePagination={true}
          handleChangePages={handleChangePages}
          placeHoldertext={"Search by name or email"}
        />
      </div>
    </div>
  );
};

export default EmployeeManagement;
